// mobile-keyboard-accessory.ts — On-screen toolbar above the soft keyboard.
//
// Renders a tight row of one-tap actions (undo/redo, outdent/indent, task,
// bullet, bold/italic that WRAP the selection, and a `/` that opens the full
// slash-command palette) just above the soft keyboard while the editor is
// focused. Active on every touch-primary device that
// isn't Electron desktop: iOS Safari / iOS PWA / iOS Capacitor, Android
// Chrome / Firefox / Samsung Internet, iPad, Android tablets.
//
// On Capacitor iOS the system input accessory bar is hidden via the
// @capacitor/keyboard plugin so this row is the only thing above the
// keyboard. capacitor.config.ts sets `resize: 'none'` so visualViewport
// reports the keyboard occlusion the same way it does in Safari, letting
// the same tracking code work in both hosts.
//
// Insertion goes through NoteEditor.dispatchChange so the edit lands in
// CodeMirror's transaction stream — preserves undo, fires onChange for
// autosave, and routes through CM6's decoration plugins for live syntax
// highlighting. The bar's bottom offset is driven by visualViewport so
// it tracks the soft keyboard's open/close animation.

import { AttachmentPicker } from '../notes/attachment-picker.js';
import { openNoteSearch } from '../notes/global-search.js';
import {
  insertLink as insertLinkCmd,
  toggleTaskLine,
  wrapSelectionToggle,
} from './format-commands.cm.js';
import { NoteEditor } from './note-editor.js';
import { insertEvent as insertEventAt } from './schedule-insert.js';
import { isTouchUI } from './touch-ui.js';

(() => {
  // Debug hook: `?mobilebar=1` (or localStorage forceMobileBar='1') force-shows
  // the bar on a desktop/fine-pointer browser, so the mobile row can be developed
  // and tested without a real touch device. Off by default; opt-in only.
  const _forceBar = (() => {
    try {
      return (
        /[?&]mobilebar=1\b/.test(location.search) || localStorage.getItem('forceMobileBar') === '1'
      );
    } catch {
      return false;
    }
  })();

  // Skip Electron (no soft keyboard) and any non-touch viewport — the shared
  // isTouchUI() predicate (which also honours the _forceBar debug force).
  if (!isTouchUI()) return;

  // Seed the --keyboard-h CSS variable to 0 so layout containers can use
  // var(--keyboard-h, 0px) safely from first paint, before init() runs and
  // before any keyboard event fires.
  document.documentElement.style.setProperty('--keyboard-h', '0px');

  // ── Hide the iOS native input accessory bar (Capacitor only) ─────────────
  // The plugin is installed by ios/package.json. On Safari/PWA it isn't
  // available and there's no programmatic way to hide the bar — we fall
  // back to the textarea's autocomplete="off" attributes (set in HTML)
  // which at least prevents the AutoFill suggestions row.
  (function hideSystemAccessoryBar() {
    const Cap = window.Capacitor;
    if (!Cap?.isNativePlatform?.()) return;
    const Keyboard = Cap.Plugins?.Keyboard;
    if (!Keyboard) return;
    try {
      // setAccessoryBarVisible is iPad-only on the native side but the JS
      // call is safe everywhere (no-op on iPhone). Some Capacitor versions
      // also expose it on iPhone; calling unconditionally is the simplest
      // stable contract.
      Keyboard.setAccessoryBarVisible({ isVisible: false });
    } catch (_) {
      /* plugin missing or older version */
    }
  })();

  // Wait for NoteEditor's CM6 view to mount before initialising. On
  // Capacitor iOS the native cold start runs slower than DOMContentLoaded,
  // so a single shot at DOMContentLoaded can land before the editor's
  // contentDOM exists — init() then bails silently and the bar never
  // appears. Poll every 100 ms for up to 5 s; bail quietly if the editor
  // really never mounts (e.g. the page has no editor at all).
  document.addEventListener('DOMContentLoaded', () => {
    let tries = 0;
    (function attempt() {
      const ce = NoteEditor?.contentDOM?.();
      const es = document.getElementById('editor-section');
      if (ce && es) {
        init();
        return;
      }
      if (tries++ < 50) setTimeout(attempt, 100);
    })();
  });

  function init(): void {
    // Idempotency: the poller can't double-mount, and a future caller
    // (e.g. workspace switch) re-firing DOMContentLoaded won't either.
    if (document.getElementById('mobile-kb-bar')) return;

    const editorSection = document.getElementById('editor-section');
    const contentEl = NoteEditor?.contentDOM?.();
    // Require the editor surface to be live in the document — a stale contentDOM
    // (detached editor) means this isn't the active editor and must not mount.
    if (!editorSection || !contentEl?.isConnected) return;

    // A fresh mount implies no in-progress gesture/customize session — start the
    // transient interaction flags clean.
    _reordering = false;
    _dragMoved = false;
    _holdConsumed = false;

    const bar = buildBar();
    // Append to <body> rather than #editor-section so a stacking context
    // change inside editor-section can't accidentally clip the fixed bar.
    document.body.appendChild(bar);
    setupReorder(bar);

    // Floating selection-format cluster (#35c). Built here so it can hook the
    // bar's hide lifecycle; hide-handle is forward-declared for hideBar/
    // keyboardGone (defined below) and assigned once the cluster is wired.
    let _hideCluster: () => void = () => {};

    let visible = false;
    let kbHeight = 0;
    // On Capacitor iOS, capacitor.config.ts uses `resize: 'none'` and the
    // WebView's visualViewport doesn't reliably shrink to reflect the
    // keyboard, so visualViewport readings of 0 don't mean the keyboard is
    // down — only the Capacitor Keyboard plugin's keyboardWillHide event
    // does. On Safari/PWA, visualViewport IS the source of truth.
    const isCapacitor = !!window.Platform?.isCapacitor;

    const show = () => {
      if (visible) return;
      visible = true;
      bar.classList.add('visible');
      updatePosition();
      updateEditorPadding();
    };
    // Hide the bar only — does NOT zero kbHeight, so the --keyboard-h CSS
    // var stays accurate while the user is typing in something other than
    // the editor (e.g. the drawer's search box) with the keyboard still up.
    const hideBar = () => {
      _hideCluster();
      if (!visible) return;
      visible = false;
      bar.classList.remove('visible');
      updateEditorPadding();
    };
    // The keyboard itself is gone — zero kbHeight + hide the bar. Called
    // from Capacitor's keyboardWillHide/keyboardDidHide and from evaluate()
    // on Safari/PWA when visualViewport reports the keyboard has retracted.
    const keyboardGone = () => {
      kbHeight = 0;
      publishKeyboardVar();
      hideBar();
    };

    function measureKeyboardHeight() {
      const vv = window.visualViewport;
      if (!vv) return 0;
      // window.innerHeight is the layout viewport (full size). vv.height
      // is the visible area excluding the keyboard. The difference is the
      // keyboard height. vv.offsetTop catches the case where iOS Safari
      // scrolls the layout viewport instead of resizing it (older
      // iOS, PWA mode).
      return Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    }

    // Publish the current keyboard height on the document root so #content-area
    // and #panel-lists can pad/shrink by it via CSS. Single source of truth so
    // visualViewport and Capacitor's keyboardWillShow can't disagree.
    function publishKeyboardVar() {
      document.documentElement.style.setProperty('--keyboard-h', `${kbHeight}px`);
    }

    function updatePosition() {
      if (!isCapacitor) {
        kbHeight = measureKeyboardHeight();
      } else {
        // On Capacitor, kbHeight is owned by the Keyboard plugin event
        // handler below. visualViewport can lie here, so don't update from
        // it — just refresh the bar's bottom from the current value.
      }
      bar.style.bottom = `${kbHeight}px`;
      publishKeyboardVar();
    }

    function updateEditorPadding() {
      const h = visible ? bar.offsetHeight : 0;
      document.documentElement.style.setProperty('--mobile-kb-bar-h', `${h}px`);
    }

    // ── Show/hide policy ─────────────────────────────────────────────────
    // Drive show/hide off the keyboard's actual presence rather than
    // textarea focus. visualViewport.resize fires when the keyboard slides
    // in or out; we re-measure and toggle accordingly. Ties the bar's
    // lifetime to the keyboard, which is what users expect.
    function evaluate() {
      // Debug force: pin the bar to the bottom of the viewport regardless of
      // keyboard/focus so it can be inspected on desktop (see _forceBar above).
      if (_forceBar) {
        show();
        bar.style.bottom = '0px';
        return;
      }
      // visualViewport is authoritative for kbHeight on Safari/PWA only.
      // On Capacitor, the Keyboard plugin events own kbHeight; visualViewport
      // here can be stale 0 and must not zero out kbHeight.
      if (!isCapacitor) {
        const kb = measureKeyboardHeight();
        if (kb === 0 && kbHeight !== 0) {
          keyboardGone();
        } else {
          kbHeight = kb;
          publishKeyboardVar();
        }
      }
      const focusedEditor = NoteEditor.hasFocus();
      if (focusedEditor && kbHeight > 0) {
        show();
        bar.style.bottom = `${kbHeight}px`;
      } else {
        hideBar();
      }
    }

    // Defer one frame on focus: at focus time the keyboard hasn't
    // animated in yet so visualViewport.height still equals window
    // height.  A second deferred check at 350ms catches the case
    // where the animation is still in flight on slower devices.
    contentEl.addEventListener('focus', () => {
      requestAnimationFrame(evaluate);
      setTimeout(evaluate, 350);
    });

    // Don't auto-hide on blur — the bar's own buttons take focus
    // momentarily (we re-focus the textarea immediately).  Keying off
    // visualViewport instead means the bar stays put through a button
    // press and only closes when the keyboard genuinely retracts.

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', evaluate);
      window.visualViewport.addEventListener('scroll', () => {
        if (visible) updatePosition();
      });
    }

    // Capacitor Keyboard plugin events are the most reliable signal on
    // iOS. Wire them up if present; they complement (not replace) the
    // visualViewport tracking above.
    const Cap = window.Capacitor;
    const Keyboard = Cap?.Plugins?.Keyboard;
    if (Keyboard && typeof Keyboard.addListener === 'function') {
      try {
        Keyboard.addListener('keyboardWillShow', (info: any) => {
          // info.keyboardHeight is in CSS pixels. On Capacitor iOS this is
          // the authoritative source because the WebView's visualViewport
          // doesn't reliably shrink with `resize: 'none'`.
          if (info && typeof info.keyboardHeight === 'number') {
            kbHeight = info.keyboardHeight;
            publishKeyboardVar();
          }
          // Show the bar even if NoteEditor.hasFocus() is false right now.
          // On native iOS keyboardWillShow can fire before CM6's
          // contenteditable focus state lands on the JS side, and gating on
          // it leaves the bar stuck hidden. Cases where the keyboard is up
          // for a non-editor input (e.g. the drawer's search box) are rare,
          // and the preview MutationObserver still hides the bar when the
          // user toggles preview.
          if (NoteEditor.hasFocus()) {
            show();
            bar.style.bottom = `${kbHeight}px`;
          }
        });
        Keyboard.addListener('keyboardDidShow', evaluate);
        Keyboard.addListener('keyboardWillHide', keyboardGone);
        Keyboard.addListener('keyboardDidHide', keyboardGone);
      } catch (e: any) {
        // Surface the failure: an empty catch here is how this regressed
        // on Capacitor in the first place. Visible in Safari Web Inspector
        // when attached to the device.
        console.warn('[mobile-kb-bar] Capacitor Keyboard listener registration failed:', e);
      }
    }

    // The view-toggle button swaps the editor for the rendered preview.
    // Hide proactively when preview takes over so the bar doesn't float
    // above non-editable content.
    const previewEl = document.getElementById('preview');
    if (previewEl) {
      const previewObserver = new MutationObserver(() => {
        if (previewEl.style.display !== 'none' && visible) hideBar();
      });
      previewObserver.observe(previewEl, { attributes: true, attributeFilter: ['style'] });
    }

    // ── Floating format cluster wiring (#35c) ────────────────────────────────
    const cluster = buildFormatCluster();
    document.body.appendChild(cluster);
    _hideCluster = () => cluster.classList.remove('visible');
    const updateCluster = () => {
      const sel = NoteEditor.getSelection?.();
      // Selection-only: hidden on a collapsed caret, without editor focus, off a
      // touch UI, or while a table cell edit owns the surface (its own toolbar).
      if (
        !sel ||
        sel.anchor === sel.head ||
        !NoteEditor.hasFocus?.() ||
        !isTouchUI() ||
        document.querySelector('.cm-widget-table-input')
      ) {
        _hideCluster();
        return;
      }
      const from = Math.min(sel.anchor, sel.head);
      const coords = NoteEditor.coordsAtPos?.(from);
      if (!coords) {
        _hideCluster();
        return;
      }
      const cw = cluster.offsetWidth || 176;
      const ch = cluster.offsetHeight || 40;
      // Float just ABOVE the selection start; flip below if there's no room.
      let top = coords.top - ch - 8;
      if (top < 6) top = (coords.bottom ?? coords.top) + 8;
      // Centre on the selection start, clamped to the viewport.
      const maxLeft = (window.innerWidth || 360) - cw - 6;
      const left = Math.max(6, Math.min(coords.left - cw / 2, maxLeft));
      cluster.style.left = `${left}px`;
      cluster.style.top = `${top}px`;
      cluster.classList.add('visible');
    };
    NoteEditor.onSelectionChange?.(updateCluster);

    // Debug force: paint the bar right away, no focus/keyboard needed.
    if (_forceBar) requestAnimationFrame(evaluate);
  }

  // ── Bar construction ─────────────────────────────────────────────────────

  // Hand-drawn monochrome icons (stroke: currentColor) so they inherit the
  // button's text colour and match the bar's clean, border-free look.
  const _svg = (paths: string): string =>
    `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;
  // Undo / redo: a curved arrow whose head points back the way the action goes.
  const UNDO_SVG = _svg('<path d="M9 14 4 9l5-5"/><path d="M4 9h9a6 6 0 0 1 0 12H6"/>');
  const REDO_SVG = _svg('<path d="M15 14 20 9l-5-5"/><path d="M20 9h-9a6 6 0 0 0 0 12h12"/>');
  // Event: a calendar.
  const EVENT_SVG = _svg(
    '<rect x="3" y="4.5" width="18" height="16.5" rx="2"/><path d="M3 9.5h18M8 2.5v4M16 2.5v4"/>'
  );
  // Highlight: a highlighter pen sweeping a line.
  const HIGHLIGHT_SVG = _svg(
    '<path d="m9 11-6 6v3h9l3-3"/><path d="m22 12-4.6 4.6a2 2 0 0 1-2.8 0l-5.2-5.2a2 2 0 0 1 0-2.8L14 4"/>'
  );
  // Indent: an arrow pushing three lines to the right.
  const INDENT_SVG = _svg('<path d="M3 8l4 4-4 4"/><path d="M11 6h10M11 12h10M11 18h10"/>');
  // Task: a checkbox with a tick.
  const TASK_SVG = _svg(
    '<rect x="3" y="3" width="18" height="18" rx="3"/><path d="m8 12 3 3 5-6"/>'
  );
  // Bullet: a bulleted list.
  const BULLET_SVG = _svg(
    '<path d="M8 6h13M8 12h13M8 18h13"/><path d="M3.5 6h.01M3.5 12h.01M3.5 18h.01"/>'
  );
  // Insert/more: a plus — the gateway to the full insert (slash) menu.
  const PLUS_SVG = _svg('<path d="M12 5v14M5 12h14"/>');
  // Outdent: mirror of INDENT_SVG — the arrow points LEFT (pull the line out).
  const OUTDENT_SVG = _svg('<path d="M7 8l-4 4 4 4"/><path d="M11 6h10M11 12h10M11 18h10"/>');
  // Dismiss: a chevron-down — collapse the soft keyboard.
  const DISMISS_SVG = _svg('<path d="M6 9l6 6 6-6"/>');
  // Wiki-link: two facing brackets.
  const WIKI_SVG = _svg(
    '<path d="M9 5H6a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h3M15 5h3a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1h-3"/>'
  );
  // Link: a chain link.
  const LINK_SVG = _svg(
    '<path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1"/><path d="M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1"/>'
  );
  // Attach: a paperclip.
  const ATTACH_SVG = _svg(
    '<path d="M21 8.5 12.5 17a3.5 3.5 0 0 1-5-5l8-8a2.3 2.3 0 0 1 3.3 3.3l-8 8a1.2 1.2 0 0 1-1.7-1.7l7.4-7.4"/>'
  );
  // Customize: sliders — the gateway to the button picker (add/remove/reorder).
  const CUSTOMIZE_SVG = _svg(
    '<path d="M4 6h9M17 6h3M4 12h3M11 12h9M4 18h5M13 18h7"/><circle cx="15" cy="6" r="2"/><circle cx="9" cy="12" r="2"/><circle cx="11" cy="18" r="2"/>'
  );
  // Find: a magnifier — opens the note-scoped find/replace side panel (#6).
  const SEARCH_SVG = _svg('<circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/>');

  // ── Button palette + active-set persistence ───────────────────────────────
  // The bar renders a USER-CHOSEN subset of a fuller palette, in a user order.
  // The active subset+order lives in ORDER_KEY; KNOWN_KEY snapshots every
  // palette action the user has already been shown, so a newly-SHIPPED button
  // surfaces once (appended) but a deliberately-REMOVED one never reappears.
  // Both are plain localStorage (NOT the synced Settings store) on purpose:
  // this bar is phone-only, so the arrangement is a device-local preference
  // that shouldn't cross-sync to the user's desktop.
  const ORDER_KEY = 'mobileKbOrder';
  const KNOWN_KEY = 'mobileKbKnown';
  let _reordering = false;
  // Set true during an active drag so the trailing click doesn't toggle a
  // button's active state; set true by a hold gesture (repeat/long-press) so
  // the trailing click doesn't ALSO run the tap action. Reset per press.
  let _dragMoved = false;
  let _holdConsumed = false;
  // The customize chip toggles reorder mode; setupReorder owns the toggle body
  // and publishes it here so the chip (built in buildBar) can call it.
  let _toggleReorderFn: (() => void) | null = null;

  // The full palette of buildable buttons. DEFAULT_ACTIVE picks the subset that
  // ships active; the rest are opt-in via the customize picker.
  const ALL_BUTTONS: any[] = [
    { action: 'undo', icon: UNDO_SVG, title: 'Undo' },
    { action: 'redo', icon: REDO_SVG, title: 'Redo' },
    { action: 'indent', icon: INDENT_SVG, title: 'Indent line' },
    { action: 'outdent', icon: OUTDENT_SVG, title: 'Outdent line' },
    { action: 'event', icon: EVENT_SVG, title: 'Event: append “ > date time” to the line' },
    { action: 'task', icon: TASK_SVG, title: 'Task: - [ ]  (long-press toggles ✓)' },
    { action: 'bullet', icon: BULLET_SVG, title: 'Bullet: -' },
    { action: 'highlight', icon: HIGHLIGHT_SVG, title: 'Highlight (wrap selection): ==…==' },
    { action: 'bold', label: 'B', title: 'Bold (wrap selection): **…**', cls: 'kb-bold' },
    { action: 'italic', label: 'I', title: 'Italic (wrap selection): *…*', cls: 'kb-italic' },
    { action: 'slash', icon: PLUS_SVG, title: 'Insert… (more options)' },
    { action: 'dismiss', icon: DISMISS_SVG, title: 'Hide keyboard' },
    { action: 'wiki', icon: WIKI_SVG, title: 'Wiki-link: [[…]]' },
    { action: 'link', icon: LINK_SVG, title: 'Link: [text](url)' },
    { action: 'attach', icon: ATTACH_SVG, title: 'Attach / photo' },
    { action: 'find', icon: SEARCH_SVG, title: 'Find in note' },
  ];

  // The buttons that ship active, in this order. The user can reorder, remove,
  // or add palette buttons from the customize picker.
  const DEFAULT_ACTIVE: string[] = [
    'undo',
    'redo',
    'indent',
    'outdent',
    'event',
    'task',
    'bullet',
    'highlight',
    'bold',
    'find',
    'slash',
    'dismiss',
  ];

  function _readJsonArray(key: string): string[] | null {
    try {
      const v = JSON.parse(localStorage.getItem(key) || 'null');
      return Array.isArray(v) ? v : null;
    } catch {
      return null;
    }
  }

  function _saveKnown(): void {
    try {
      localStorage.setItem(KNOWN_KEY, JSON.stringify(ALL_BUTTONS.map((b) => b.action)));
    } catch {
      /* storage unavailable */
    }
  }

  // Resolve the ACTIVE ordered action list from persisted state. Drops
  // unknown/removed ids from the saved order, then appends any palette action
  // the user hasn't been shown yet (absent from KNOWN) so a newly-shipped button
  // surfaces once — when KNOWN is absent (first load after the palette shipped),
  // every action is "new", so an existing user gains the fuller set once WITHOUT
  // losing their custom order. Falls back to DEFAULT_ACTIVE when nothing saved.
  function _loadActive(): string[] {
    const palette = ALL_BUTTONS.map((b) => b.action);
    const paletteSet = new Set(palette);
    const savedRaw = _readJsonArray(ORDER_KEY);
    let active: string[];
    if (savedRaw) {
      const seen = new Set<string>();
      active = [];
      for (const a of savedRaw) {
        if (paletteSet.has(a) && !seen.has(a)) {
          seen.add(a);
          active.push(a);
        }
      }
      const known = new Set(_readJsonArray(KNOWN_KEY) || []);
      for (const a of palette) if (!known.has(a) && !active.includes(a)) active.push(a);
      if (active.length === 0) active = DEFAULT_ACTIVE.slice();
    } else {
      active = DEFAULT_ACTIVE.filter((a) => paletteSet.has(a));
    }
    _saveKnown();
    return active;
  }

  // Persist the ACTIVE (non-dimmed, data-action) buttons in DOM order. The
  // customize chip has no data-action, so it never lands in the saved set.
  function _saveActive(bar: HTMLElement): void {
    try {
      const actions = [
        ...bar.querySelectorAll('.mobile-kb-btn[data-action]:not(.kb-inactive)'),
      ].map((el: any) => el.dataset.action);
      localStorage.setItem(ORDER_KEY, JSON.stringify(actions));
    } catch {
      /* storage unavailable — order just isn't remembered */
    }
  }

  // Toggle a palette button in/out of the active set (customize mode). A removal
  // is refused when it would empty the bar (floor at 1). Persists after.
  function _toggleActive(bar: HTMLElement, btn: HTMLElement): void {
    if (btn.classList.contains('kb-inactive')) {
      btn.classList.remove('kb-inactive');
    } else {
      const activeCount = bar.querySelectorAll(
        '.mobile-kb-btn[data-action]:not(.kb-inactive)'
      ).length;
      if (activeCount <= 1) return; // never leave the bar empty
      btn.classList.add('kb-inactive');
    }
    _saveActive(bar);
  }

  function buildBar(): HTMLElement {
    const bar = document.createElement('div');
    bar.id = 'mobile-kb-bar';
    bar.className = 'mobile-kb-bar';
    bar.setAttribute('data-ui-only', 'true');
    bar.setAttribute('role', 'toolbar');
    bar.setAttribute('aria-label', 'Editor insertions');

    const active = _loadActive();
    const activeSet = new Set(active);
    const byAction = new Map(ALL_BUTTONS.map((b) => [b.action, b]));
    // Active buttons first (in the saved order), then the inactive palette
    // buttons — the latter stay hidden (CSS) until the customize chip opens the
    // picker, where they appear dimmed and tappable to add.
    const renderList = [
      ...active.map((a) => byAction.get(a)).filter(Boolean),
      ...ALL_BUTTONS.filter((b) => !activeSet.has(b.action)),
    ];

    for (const b of renderList) {
      // Render as a non-focusable <div role="button"> rather than a real
      // <button>. iOS Safari moves focus to a tapped <button> (even with
      // tabindex=-1), which blurs the textarea and kicks off the
      // keyboard's hide animation.  A div without tabindex is not in the
      // focus pipeline at all, so the textarea stays focused through the
      // tap — meaning we DON'T need touchstart preventDefault, which was
      // the reason horizontal scroll on the bar broke after a press.
      const btn = document.createElement('div');
      btn.setAttribute('role', 'button');
      btn.className = `mobile-kb-btn${b.cls ? ` ${b.cls}` : ''}`;
      if (!activeSet.has(b.action)) btn.classList.add('kb-inactive');
      btn.dataset.action = b.action;
      if (b.icon) btn.innerHTML = b.icon;
      else btn.textContent = b.label;
      btn.title = b.title;
      btn.setAttribute('aria-label', b.title);

      // mousedown preventDefault keeps focus on a desktop touch laptop
      // (where the synthesized mouse path could still steal focus), but
      // we deliberately leave touchstart alone so the bar's overflow-x
      // scroller receives the gesture when the user swipes between
      // buttons.
      btn.addEventListener('mousedown', (e) => e.preventDefault());

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (_reordering) {
          // In customize mode a tap toggles the button's membership (a real
          // drag sets _dragMoved, which suppresses the toggle).
          if (!_dragMoved) _toggleActive(bar, btn);
          return;
        }
        if (_holdConsumed) {
          _holdConsumed = false;
          return; // a hold gesture (repeat / long-press) already handled this press
        }
        runAction(b.action);
      });
      bar.appendChild(btn);
    }

    // Trailing customize chip — a PERMANENT affordance that opens the button
    // picker (reorder + add/remove). It is NOT part of the reorderable/removable
    // set: no data-action (so it never lands in the saved order) and its own
    // click handler bypasses the reorder tap-guard so it can also EXIT the mode.
    const chip = document.createElement('div');
    chip.setAttribute('role', 'button');
    chip.className = 'mobile-kb-btn mobile-kb-customize';
    chip.innerHTML = CUSTOMIZE_SVG;
    chip.title = 'Customize toolbar';
    chip.setAttribute('aria-label', 'Customize toolbar');
    chip.addEventListener('mousedown', (e) => e.preventDefault());
    chip.addEventListener('click', (e) => {
      e.preventDefault();
      _toggleReorderFn?.();
    });
    bar.appendChild(chip);

    return bar;
  }

  // Wire drag-to-reorder (only while in customize mode, entered via the chip),
  // the freed long-press → hold-to-repeat undo/redo + task long-press-toggle,
  // and tap-off-the-bar to save+exit.
  function setupReorder(bar: HTMLElement): void {
    let drag: any = null; // { btn, fromIndex, toIndex, startX, slots, spacing }
    let hold: any = null; // { x, y } — an in-flight press being timed
    let holdTimer: any = null;
    let holdInterval: any = null;

    // The reorderable buttons — every palette button (active OR inactive) that
    // carries a data-action. Excludes the permanent, non-draggable customize
    // chip (which has no data-action).
    const buttonsOf = () =>
      [...bar.querySelectorAll('.mobile-kb-btn[data-action]')] as HTMLElement[];

    function enterReorder(): void {
      if (_reordering) return;
      _reordering = true;
      bar.classList.add('reordering');
    }

    function exitReorder(save: boolean): void {
      if (!_reordering) return;
      _reordering = false;
      bar.classList.remove('reordering', 'bar-dragging');
      buttonsOf().forEach((el) => {
        el.style.transform = '';
        el.classList.remove('kb-dragging');
      });
      if (save) _saveActive(bar);
    }

    // Published so the customize chip (built in buildBar) can toggle the mode.
    _toggleReorderFn = () => {
      if (_reordering) exitReorder(true);
      else enterReorder();
    };

    // ── Hold gestures (long-press freed from reorder) ─────────────────────────
    function clearHold(): void {
      if (holdTimer) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
      if (holdInterval) {
        clearInterval(holdInterval);
        holdInterval = null;
      }
      hold = null;
    }
    function startHold(btn: HTMLElement, clientX: number, clientY: number): void {
      const action = btn.dataset.action;
      hold = { x: clientX, y: clientY };
      if (action === 'undo' || action === 'redo') {
        // Hold-to-repeat: after a threshold, fire repeatedly until release. The
        // quick-tap path still fires exactly one step via the click handler
        // (the interval only starts once _holdConsumed is set here).
        holdTimer = setTimeout(() => {
          holdTimer = null;
          _holdConsumed = true;
          runHistory(action);
          holdInterval = setInterval(() => runHistory(action), 120);
        }, 400);
      } else if (action === 'task') {
        // Long-press → toggle the checkbox on the current task line(s) (#15's
        // shared toggle); a quick tap still inserts a fresh `- [ ] `.
        holdTimer = setTimeout(() => {
          holdTimer = null;
          _holdConsumed = true;
          toggleTaskAtCursor();
        }, 500);
      }
    }

    function startDrag(btn: HTMLElement, clientX: number, ptrId: number): void {
      const els = buttonsOf();
      const slots = els.map((el) => {
        const r = el.getBoundingClientRect();
        return { el, center: r.left + r.width / 2 };
      });
      const fromIndex = els.indexOf(btn);
      const spacing = slots.length > 1 ? slots[1].center - slots[0].center : 0;
      drag = { btn, fromIndex, toIndex: fromIndex, startX: clientX, slots, spacing };
      bar.classList.add('bar-dragging');
      btn.classList.add('kb-dragging');
      try {
        btn.setPointerCapture(ptrId);
      } catch {
        /* capture can fail mid-gesture; bar-level listeners still track it */
      }
    }

    function moveDrag(clientX: number): void {
      if (!drag) return;
      const { slots, fromIndex, spacing, btn } = drag;
      const n = slots.length;
      const dx = clientX - drag.startX;
      if (Math.abs(dx) > 4) _dragMoved = true; // a real drag → suppress the tap-toggle
      // The dragged button tracks the finger every frame.
      btn.style.transform = `translateX(${dx}px) scale(1.12)`;
      // Find the target slot; the other buttons only shift when it actually
      // changes (a slot-boundary crossing), so don't rewrite their transforms
      // on every move event.
      const dc = slots[fromIndex].center + dx; // dragged button's live centre
      let to = fromIndex;
      while (to < n - 1 && dc > slots[to + 1].center) to++;
      while (to > 0 && dc < slots[to - 1].center) to--;
      if (to === drag.toIndex) return;
      drag.toIndex = to;
      slots.forEach((s: any, d: number) => {
        if (s.el === btn) return;
        // Shift the buttons the dragged one has crossed, to open the gap.
        let shift = 0;
        if (fromIndex < to && d > fromIndex && d <= to) shift = -spacing;
        else if (fromIndex > to && d >= to && d < fromIndex) shift = spacing;
        s.el.style.transform = shift ? `translateX(${shift}px)` : '';
      });
    }

    function endDrag(): void {
      if (!drag) return;
      const { slots, fromIndex, toIndex, btn } = drag;
      const els = slots.map((s: any) => s.el);
      const [moved] = els.splice(fromIndex, 1);
      els.splice(toIndex, 0, moved);
      slots.forEach((s: any) => {
        s.el.style.transform = '';
      });
      btn.classList.remove('kb-dragging');
      bar.classList.remove('bar-dragging');
      els.forEach((el: HTMLElement) => {
        bar.appendChild(el);
      }); // commit the new DOM order
      // Keep the customize chip trailing after the buttons were re-appended.
      const chip = bar.querySelector('.mobile-kb-customize');
      if (chip) bar.appendChild(chip);
      drag = null;
      // Stay in reorder mode; the order is persisted on exit (tap off the bar).
    }

    bar.addEventListener('pointerdown', (e: any) => {
      const btn = (e.target as HTMLElement).closest('.mobile-kb-btn') as HTMLElement | null;
      // The customize chip drives its own click handler — never drag/hold it.
      if (!btn || btn.classList.contains('mobile-kb-customize')) return;
      if (_reordering) {
        _dragMoved = false;
        startDrag(btn, e.clientX, e.pointerId);
        return;
      }
      startHold(btn, e.clientX, e.clientY);
    });

    bar.addEventListener('pointermove', (e: any) => {
      if (hold) {
        // A pre-threshold move means a scroll/swipe, not a hold — cancel it.
        if (Math.abs(e.clientX - hold.x) > 10 || Math.abs(e.clientY - hold.y) > 10) clearHold();
      }
      if (drag) {
        e.preventDefault();
        moveDrag(e.clientX);
      }
    });

    const end = (): void => {
      clearHold();
      if (drag) endDrag();
    };
    bar.addEventListener('pointerup', end);
    bar.addEventListener('pointercancel', end);
    bar.addEventListener('pointerleave', end);

    // Any pointer down off the bar commits the order and leaves reorder mode.
    // Capture phase so we see it before the target's own handlers.
    document.addEventListener(
      'pointerdown',
      (e: any) => {
        if (_reordering && !bar.contains(e.target)) exitReorder(true);
      },
      true
    );
  }

  // ── Action dispatch ─────────────────────────────────────────────────────

  function runAction(action: any): any {
    switch (action) {
      case 'undo':
        return runHistory('undo');
      case 'redo':
        return runHistory('redo');
      case 'indent':
        return NoteEditor.indent();
      case 'outdent':
        return NoteEditor.outdent();
      case 'task':
        return insertAtLineStart('- [ ] ');
      case 'bullet':
        return insertAtLineStart('- ');
      case 'bold':
        return wrapInline('**');
      case 'italic':
        return wrapInline('*');
      case 'highlight':
        return wrapInline('==');
      case 'event':
        return insertEvent();
      case 'wiki':
        return openWikiLink();
      case 'link':
        return insertMarkdownLink();
      case 'attach':
        return openAttachmentPicker();
      case 'find':
        return openNoteSearch();
      case 'slash':
        return openSlashMenu();
      case 'dismiss':
        return dismissKeyboard();
    }
  }

  // ── Insertion helpers ───────────────────────────────────────────────────
  // Goes through NoteEditor.dispatchChange so the edit lands in CodeMirror's
  // transaction stream (preserves undo, fires onChange for autosave, runs
  // through the decoration plugins).

  // Undo/redo via the NoteEditor facade, which runs CM6's history commands on
  // the live view — the same stack as ⌘Z / the history keymap (one tap = one
  // step). The bar's mousedown-preventDefault keeps the editor focused.
  function runHistory(which: 'undo' | 'redo'): void {
    if (which === 'undo') NoteEditor.undo();
    else NoteEditor.redo();
  }

  // Wrap (or, on a second press, UNWRAP) the current selection in `marker` — the
  // shared, toggle-aware `wrapSelectionToggle` from format-commands.cm.ts (the
  // SAME verb the desktop Mod-b/i/e keymap uses). Empty selection → an empty
  // pair with the caret between. Shared by the bar's bold/italic/highlight and
  // the floating format cluster.
  function wrapInline(marker: string): void {
    const view = NoteEditor._view?.();
    if (view) wrapSelectionToggle(view, marker);
  }

  // Long-press on the task button flips the checkbox on the current line(s) via
  // the shared task-toggle command (identical to a checkbox click, incl. its
  // `input.task` userEvent).
  function toggleTaskAtCursor(): void {
    const view = NoteEditor._view?.();
    if (view) toggleTaskLine(view);
  }

  // Insert `[[` at the caret and open the note-name completion — the mobile
  // gateway to a wiki-link (mirrors openSlashMenu but with the `[[` trigger).
  function openWikiLink(): void {
    const sel = NoteEditor.getSelection();
    const from = Math.min(sel.anchor, sel.head);
    const to = Math.max(sel.anchor, sel.head);
    NoteEditor.dispatchChange({ from, to, insert: '[[' }, from + 2);
    NoteEditor.triggerCompletion?.();
  }

  // Wrap the selection as a markdown `[text](url)` with the caret in the url via
  // the shared insertLink command; with no selection it inserts `[]()`.
  function insertMarkdownLink(): void {
    const view = NoteEditor._view?.();
    if (view) insertLinkCmd(view);
  }

  // Open the platform attachment picker (web input / iOS camera+files); the
  // picked file routes through AttachmentInput — the same funnel /attach uses.
  function openAttachmentPicker(): void {
    AttachmentPicker?.pick?.();
  }

  // Drop DOM focus (Safari/PWA retract the keyboard on blur) and, on Capacitor
  // where blur alone may not close the native keyboard, also ask the Keyboard
  // plugin to hide. The existing visualViewport / keyboardWillHide handlers then
  // fire keyboardGone() and hide the bar — no extra teardown here.
  function dismissKeyboard(): void {
    try {
      NoteEditor.contentDOM()?.blur();
    } catch {
      /* contentDOM missing — nothing to blur */
    }
    try {
      window.Capacitor?.Plugins?.Keyboard?.hide?.();
    } catch {
      /* plugin missing or older version */
    }
  }

  // Insert a `/` at the caret and open the slash-command menu — the mobile
  // gateway to the full insert palette (the same menu `/` typing triggers).
  function openSlashMenu(): void {
    const sel = NoteEditor.getSelection();
    const from = Math.min(sel.anchor, sel.head);
    const to = Math.max(sel.anchor, sel.head);
    NoteEditor.dispatchChange({ from, to, insert: '/' }, from + 1);
    NoteEditor.triggerCompletion?.();
  }

  // ── Floating selection-format cluster (#35c) ─────────────────────────────
  // A tiny toolbar (Bold / Italic / Highlight / Link) that floats just above a
  // non-empty selection on touch UIs. Same non-focusable div + mousedown-
  // preventDefault pattern as the bar so tapping doesn't blur the editor.
  function buildFormatCluster(): HTMLElement {
    const cluster = document.createElement('div');
    cluster.id = 'mobile-format-cluster';
    cluster.className = 'mobile-format-cluster';
    cluster.setAttribute('data-ui-only', 'true');
    cluster.setAttribute('role', 'toolbar');
    cluster.setAttribute('aria-label', 'Format selection');
    const items: any[] = [
      { key: 'bold', label: 'B', title: 'Bold', cls: 'kb-bold', run: () => wrapInline('**') },
      { key: 'italic', label: 'I', title: 'Italic', cls: 'kb-italic', run: () => wrapInline('*') },
      { key: 'highlight', icon: HIGHLIGHT_SVG, title: 'Highlight', run: () => wrapInline('==') },
      { key: 'link', icon: LINK_SVG, title: 'Link', run: insertMarkdownLink },
    ];
    for (const it of items) {
      const btn = document.createElement('div');
      btn.setAttribute('role', 'button');
      btn.className = `mobile-kb-btn mobile-fmt-btn${it.cls ? ` ${it.cls}` : ''}`;
      btn.dataset.fmt = it.key;
      if (it.icon) btn.innerHTML = it.icon;
      else btn.textContent = it.label;
      btn.title = it.title;
      btn.setAttribute('aria-label', it.title);
      btn.addEventListener('mousedown', (e) => e.preventDefault());
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        it.run();
      });
      cluster.appendChild(btn);
    }
    return cluster;
  }

  function insertAtLineStart(prefix: string): void {
    const sel = NoteEditor.getSelection();
    const value = NoteEditor.getContent();
    const lineStart = value.lastIndexOf('\n', sel.anchor - 1) + 1;
    NoteEditor.dispatchChange(
      { from: lineStart, to: lineStart, insert: prefix },
      sel.anchor + prefix.length
    );
  }

  // Append a schedule marker " > YYMMDD HHMM HHMM" (today; now → now+1h, minutes
  // rounded to 5) at the END of the current line, caret left just before it so
  // the user can type/extend the description — which must precede the `>`.
  // Mirrors the `/event` slash command's timed template.
  function insertEvent(): void {
    const view = NoteEditor._view?.();
    if (!view) return;
    const caret = NoteEditor.getSelection().anchor;
    // Shared with the /schedule slash command (schedule-insert.ts): append the
    // timed stamp at the line end with the caret before it — passing caret ===
    // caret so nothing is removed.
    insertEventAt(view, caret, caret);
  }
})();
